import { Component, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { SharedModule } from 'app/shared';
import { Resource } from 'app/shared/acl/acl.model';
import { MyAutopropApi } from 'app/shared/service/my-autoprop-api.service';
import { SupportService } from 'app/shared/support/support.service';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { OverlayRef } from 'app/shared/ui/overlays';
import { BillingService } from '../my-autoprop/billing/billing.service';


@Component({
    selector: 'feature-subscription-card',
    imports: [
        SharedModule,
    ],
    host: {
        class: 'card p-4 w-100',
    },
    template: `
        <h2>{{ name() }}</h2>
        <div class="text-bold mb-2">
            <span class="text-large">{{ '$' + price() }} plus tax</span> / annual
        </div>
        <div class="d-flex mb-3">
            <button *apAcl="r.UPGRADE_NOTIF_AREA" class="btn btn-primary w-50" (click)="subscribe()">
                Subscribe
            </button>
            <button *apAcl="r.UPGRADE_NOTIF_AREA; behavior: 'hide-if-granted'" class="btn btn-primary w-50" (click)="contactUs()">
                Contact Us
            </button>
            <a [href]="learnMore()" target="_blank" class="btn btn-link">
                Learn More
            </a>
        </div>
        <ng-content></ng-content>
    `,
})
export class FeatureSubscriptionCardComponent {

    name = input.required<string>();

    price = input.required<number>();

    learnMore = input.required<string>();

    stripeId = input.required<string>();

    subscribed = output<void>();

    r = Resource;

    private billingService = inject(BillingService);

    private supportService = inject(SupportService);

    private modalsService = inject(ModalsService);

    contactUs() {
        this.supportService.openSupport(`Hi, I’m interested in the ${this.name()} Notifications subscription`);
    }

    subscribe() {
        const { result } = this.billingService.openOrderSummaryBasicSubscription(this.stripeId(), this.name(), this.price());
        result.subscribe(() => {
            this.modalsService.alert(
                `You have been subscribed to ${this.name()} product`,
                'Payment Successful',
                'success',
            );
            this.subscribed.emit();
        });
    }
}


type Item = {
    included: boolean;
    label: string;
};


@Component({
    selector: 'feature-list',
    template: `
        @for (item of list(); track item.label) {
            <div class="d-flex align-items-baseline">
                @if (item.included) {
                    <div class="mr-2">
                        <img src="/assets/images/tick-mark.svg">
                    </div>
                }
                @else {
                    <div class="mr-2">
                        <img src="/assets/images/cross-mark.svg">
                    </div>
                }
                <div [innerHTML]="item.label" class="py-2"></div>
            </div>
        }
    `,
})
export class FeatureListComponent {

    list = input.required<Item[]>();
}


@Component({
    selector: 'feature-subscription-modal',
    imports: [
        RouterLink,
        SharedModule,
        FeatureSubscriptionCardComponent,
        FeatureListComponent,
    ],
    template: `
        <ui-modal size="1100">
            <modal-header>
                Feature Subscriptions
            </modal-header>
            <modal-body>
                <div class="p-2">
                    <h3>
                        AUTOPROP Notifications
                    </h3>
                    <div class="row">
                        @for (card of cards(); track card.name) {
                            <div class="col-md-4 d-flex">
                                <feature-subscription-card
                                    [name]="card.name"
                                    [price]="card.price"
                                    [stripeId]="card.stripeId"
                                    [learnMore]="card.learnMore"
                                    (subscribed)="close()">
                                    <p [innerHTML]="card.description"></p>
                                    <feature-list [list]="card.items"></feature-list>
                                    @if (card.includes) {
                                        <div class="mt-3">
                                            Includes:
                                            <ul class="mt-3">
                                                @for (item of card.includes; track item) {
                                                    <li class="mt-3">{{ item }}</li>
                                                }
                                            </ul>
                                        </div>
                                    }
                                </feature-subscription-card>
                            </div>
                        }
                        @empty {
                            <div class="d-flex align-items-center justify-content-center w-100">
                                <ui-block-loading></ui-block-loading>
                            </div>
                        }
                    </div>
                </div>
            </modal-body>
            <modal-footer>
                <button class="btn btn-link" (click)="close()">
                    Close
                </button>
                <a class="btn btn-secondary ml-auto" [routerLink]="['/dashboard', 'my-autoprop', 'billing']" (click)="close()">
                    Manage Subscriptions
                </a>
            </modal-footer>
        </ui-modal>
    `,
})
export class FeatureSubscriptionModalComponent {

    private overlayRef = inject(OverlayRef);

    private myAutopropApi = inject(MyAutopropApi);

    cards = toSignal(this.myAutopropApi.getNotificationsBundles());

    close() {
        this.overlayRef.dismiss('close');
    }
}
